import { Button, Grid, GridItem, HStack, Text } from '@chakra-ui/react';
import {
  add,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';
import { map } from 'lodash';
import { createElement, useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

import dateTransformer from '../../helpers/dateTransformer';
import { PlainDropdown } from '../../helpers/general';
import useAnalyticsQuery from '../../hooks/useAnalyticsQuery';
import { ContentCard } from '../../layout/ContentCard';

const getUnitDropdown = (horizon: string) => {
  const units = [
    { value: 'day', label: getI18n().t('forms:date.day') },
    { value: 'week', label: getI18n().t('forms:date.week') },
    { value: 'month', label: getI18n().t('forms:date.month') },
    { value: 'year', label: getI18n().t('forms:date.year') },
  ];

  const index = units.findIndex((obj) => {
    return obj.value === horizon;
  });

  return units.slice(horizon === 'year' ? 1 : 0, index);
};

const getInterval = (unit: string, start?: Date, end?: Date) => {
  if (!start || !end || start === end) {
    return [];
  }
  let returnData: any = [];
  let interval = [];
  let labelFormat = 'dd/MM/yyyy';
  switch (unit) {
    case 'week':
      interval = eachWeekOfInterval({ start: start, end });
      labelFormat = 'w';
      break;
    case 'month':
      interval = eachMonthOfInterval({ start: subDays(start, 1), end });
      labelFormat = 'MMM/yy';
      break;
    case 'year':
      interval = eachYearOfInterval({ start: subDays(start, 1), end });
      labelFormat = 'y';
      break;
    default:
      interval = eachDayOfInterval({ start: subDays(start, 1), end });
  }
  interval.forEach((step) => {
    returnData.push({ label: format(step, labelFormat) });
  });
  return returnData;
};
const KPIChartCard = (props: { kpis: Array<string>; header: string; description?: string; queryKey: string; chart: any }) => {
  const { t } = useTranslation();
  const { queryKey, kpis, header, description, chart } = props;
  const today = useMemo(() => new Date(), []);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [unit, setUnit] = useState('day');
  const [horizon, setHorizon] = useState('week');
  const [step, setStep] = useState(0);

  const analyticsQuery = useAnalyticsQuery(start, end, unit, kpis.join(), queryKey);

  useEffect(() => {
    setStep(0);
  }, [horizon]);

  useEffect(() => {
    let date = today;
    if (step !== 0) {
      date = add(today, { [horizon + 's']: step, hours: 24 });
    }

    const units = map(getUnitDropdown(horizon), 'value');
    if (!units.includes(unit)) {
      setUnit(units[units.length - 1]);
    }
    switch (horizon) {
      case 'month':
        setStart(startOfMonth(date));
        setEnd(endOfMonth(date));
        break;
      case 'year':
        setStart(startOfYear(date));
        setEnd(endOfYear(date));
        break;
      default:
        setStart(startOfWeek(date, { weekStartsOn: 1 }));
        setEnd(endOfWeek(date, { weekStartsOn: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, horizon]);

  return (
    <ContentCard p={4} header={header} w={'85%'} contentDescription={description} isFetching={analyticsQuery.isFetching || analyticsQuery.isLoading}>
      <Grid templateColumns="repeat(3, 1fr)" gap={1}>
        <GridItem colSpan={2} textAlign={'center'}>
          <HStack justifyContent={'center'}>
            <Button size={'sm'} variant={'outline'} onClick={() => setStep(step - 1)}>
              {' '}
              {'<'}{' '}
            </Button>
            {PlainDropdown({
              options: [
                { value: 'week', label: t('forms:date.week') },
                { value: 'month', label: t('forms:date.month') },
                { value: 'year', label: t('forms:date.year') },
              ],
              onChange: setHorizon,
              value: horizon,
            })}
            <Button size={'sm'} variant={'outline'} onClick={() => setStep(step + 1)}>
              {' '}
              {'>'}{' '}
            </Button>
          </HStack>
          <Text alignSelf={'end'}>
            {dateTransformer(start)} - {dateTransformer(end)}
          </Text>
        </GridItem>
        <GridItem colSpan={1} justifySelf={'end'}>
          <Text>{t('controlling:filters.unit.label')}</Text>
          {PlainDropdown({
            options: getUnitDropdown(horizon),
            onChange: setUnit,
            value: unit,
          })}
        </GridItem>
      </Grid>
      {createElement(chart, {
        data: analyticsQuery?.data,
        loadingDataInterval: getInterval(unit, start, end),
      })}
    </ContentCard>
  );
};

export default KPIChartCard;
